// RelationQuickAddModal.jsx

import React, { useEffect, useMemo, useState } from 'react';

import { toast } from 'react-toastify';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { getSingularLabel } from 'utilities/Languages';
import { createNode as createNodeFromType } from 'utilities/NodeFactory';
import { resolveCreateNodeModule } from 'utilities/createNodeModuleContext';
import { resolveNodeModule } from 'utilities/nodeModule';

// Services
import { createNode as createNodeDoc } from 'services/nodes/NodeWriteService';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import TextInput from 'components/textinput/TextInput';

/**
 * RelationQuickAddModal
 *
 * Title-only create for Relation pickers. Creates a node, then calls
 * onCreated — no Peek, no hierarchy edge (the Relation field is the link).
 *
 * @param {boolean} isOpen
 * @param {function} setOpen
 * @param {string} typeKey
 * @param {string|null} baseKey
 * @param {object|null} parentNode - Parent form node (module inheritance).
 * @param {string} [initialTitle]
 * @param {function} [onCreated] - Called with the new node after save.
 *
 * @returns {ReactNode}
 */
const RelationQuickAddModal = ({
  isOpen,
  setOpen,
  typeKey,
  baseKey = null,
  parentNode = null,
  initialTitle = '',
  onCreated,
}) => {
  const { getType } = useNodeTypes();
  const { user } = useUser();
  const isMobile = useIsMobile();

  const [title, setTitle] = useState('');
  const [titleError, setTitleError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const typeDef = useMemo(() => (
    typeKey ? getType(typeKey) : null
  ), [getType, typeKey]);

  const singularLabel = useMemo(() => (
    getSingularLabel(typeDef, typeKey || 'Item', 'en') || 'Item'
  ), [typeDef, typeKey]);

  useEffect(() => {
    if (!isOpen) {
      setTitle('');
      setTitleError('');
      setIsSaving(false);
      return;
    }
    setTitle(String(initialTitle || ''));
    setTitleError('');
    setIsSaving(false);
  }, [initialTitle, isOpen]);

  const handleSave = async () => {
    if (isSaving || !typeKey || !user?.key) return;

    const trimmedTitle = String(title || '').trim();
    if (!trimmedTitle) {
      setTitleError('Title is required.');
      return;
    }

    if (!baseKey) {
      setTitleError('No active space.');
      return;
    }

    setTitleError('');
    setIsSaving(true);

    try {
      const nodeModule = parentNode?.key
        ? resolveNodeModule(parentNode)
        : resolveCreateNodeModule({ consume: true });
      const newNode = await createNodeFromType(
        typeKey,
        user,
        baseKey,
        { module: nodeModule },
      );
      newNode.title = trimmedTitle;

      await createNodeDoc(newNode.key, newNode);

      setOpen?.(false);
      onCreated?.(newNode);
      toast.success('Saved.');
    } catch (error) {
      console.error('[RelationQuickAddModal] Failed to save node:', error);
      toast.error(`Could not save: ${error?.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      title={`Add ${singularLabel}`}
      isOpen={isOpen}
      width={isMobile ? '100%' : 420}
      height="auto"
      onClose={() => {
        if (isSaving) return;
        setOpen?.(false);
      }}
    >
      <ModalForm>
        <TextInput
          value={title}
          placeholder="Title"
          helperText={titleError}
          error={!!titleError}
          autoFocus
          autoSelect
          maxLength={160}
          onChange={(event) => {
            setTitle(event.target.value);
            if (titleError) setTitleError('');
          }}
          onEnterPress={() => {
            handleSave();
          }}
          onEscapePress={() => {
            if (isSaving) return;
            setOpen?.(false);
          }}
        />
      </ModalForm>

      <ModalButtons>
        <Button
          label="Cancel"
          enabled={!isSaving}
          height="44px"
          onClick={() => setOpen?.(false)}
        />
        <Button
          label={isSaving ? 'Saving...' : 'Save'}
          enabled={!isSaving}
          height="44px"
          onClick={handleSave}
        />
      </ModalButtons>
    </Modal>
  );
};

RelationQuickAddModal.displayName = 'RelationQuickAddModal';

export default RelationQuickAddModal;
